import {useRouter} from 'next/router';
import {useCallback, useEffect, useRef, useState} from 'react';
import {MOBILE_LAYOUT_ID} from '../../../design-system/layout/constant';

export type ScrollDirectionType = 'IDLE' | 'DOWN' | 'UP';

export interface ScrollInfo {
  direction: ScrollDirectionType;
  isScrolledEnough: boolean;
}

export function useGetScrollInfo(enabled: boolean = true): ScrollInfo {
  const [scrollDirection, setScrollDirection] =
    useState<ScrollDirectionType>('IDLE');
  const [isScrolledEnough, setIsScrolledEnough] = useState(false);
  const lastScrollYRef = useRef(0);
  const router = useRouter();

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled || !router.asPath) {
      return;
    }

    setScrollDirection('IDLE');
  }, [router.asPath, enabled]);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const lastScrollY = lastScrollYRef.current;
    /**
     * ios에서 페이지 최하단에서 아래로 스크롤 시도할 경우 스크롤이 살짝 위로 올라가는 eliastic scroll 현상이 발생합니다.
     * 이때 direction이 UP으로 변경되면서 상단메뉴가 노출되기 때문에 해당 상황에서는 상단메뉴를 노출하지 않도록 처리합니다.
     */
    const maxScrollY =
      document.documentElement.scrollHeight - window.innerHeight;
    if (currentScrollY >= maxScrollY) {
      return;
    }
    /**
     * 최상단에서 아주 살짝만 스크롤할 경우 상단 메뉴는 숨김처리되지만 TabPositionControl의 경우 최상단에 sticky되지 않아서 빈 공간이 노출됩니다.
     * 이를 해결하기 위해 상단 메뉴 영역만큼 스크롤하기 전에는 상단 메뉴를 숨김처리하지 않습니다.
     */
    const mobileLayout = document.getElementById(MOBILE_LAYOUT_ID);
    if (mobileLayout) {
      const scrollYThreshold = mobileLayout.offsetHeight;
      if (currentScrollY >= scrollYThreshold) {
        setIsScrolledEnough(true);
      } else {
        setIsScrolledEnough(false);
      }
    }

    // 같은 위치면 이전 상태 유지
    if (currentScrollY === lastScrollY) {
      return;
    }

    if (currentScrollY > lastScrollY) {
      setScrollDirection('DOWN');
    } else if (currentScrollY < lastScrollY) {
      setScrollDirection('UP');
    }

    lastScrollYRef.current = currentScrollY;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) {
      return;
    }

    lastScrollYRef.current = window.scrollY;
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, enabled]);

  return {direction: scrollDirection, isScrolledEnough};
}
